import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { keycloak } from '@/auth';
import { API_BASE_URL } from '@/api/client';
import { getUnreadCount } from '@/api/employee-notifications';
import { formatNotificationMessage } from '@shared/types';
import type { EmployeeNotificationDTO } from '@shared/types';

export function useNotifications() {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['employee-notifications', 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!keycloak.token) return;

    let wsUrl = API_BASE_URL;
    if (wsUrl.endsWith('/api')) {
      wsUrl = wsUrl.slice(0, -4);
    }
    if (!wsUrl || wsUrl.startsWith('/')) {
      wsUrl = window.location.origin;
    }

    const sock = io(`${wsUrl}/employee-notifications`, {
      auth: { token: keycloak.token },
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    sock.on('notification', (notification: EmployeeNotificationDTO) => {
      const message = formatNotificationMessage(notification.type, notification.payload);
      toast.info(message);

      queryClient.invalidateQueries({ queryKey: ['employee-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
    });

    socketRef.current = sock;

    return () => {
      sock.disconnect();
      socketRef.current = null;
    };
  }, [keycloak.token, queryClient]);

  return { unreadCount };
}
